import type { CoachingSession, CoachProfile } from '@efdigital/ll-addon-data';
import { EmptyState } from '@efdigital/ll-xceed-v2/components/application/empty-state';
import { Spinner } from '@efdigital/ll-xceed-v2/components/application/spinner.gts';
import SessionCard from '@efdigital/ll-xceed-v2/components/coaching/coach/session-card.gts';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import { toCoachSessionCardItems } from '@efdigital/ll-xceed-v2/utils/coaching/coach-session-card-items';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { Await } from '@warp-drive/ember';

interface CoacheeSessionGroupSignature {
  Args: {
    title?: string;
    sessions: CoachingSession[];
    coach: CoachProfile;
    emptyMessage?: string;
    onChanged?: () => void | Promise<void>;
  };
  Element: HTMLDivElement;
}

/** One group of session cards on a coachee's page. */
export default class CoacheeSessionGroup extends Component<CoacheeSessionGroupSignature> {
  @service declare coach: CoachService;
  @service declare router: RouterService;

  cardItems = (sessions: CoachingSession[]) =>
    toCoachSessionCardItems(sessions, this.coach);

  refresh = () => {
    if (this.args.onChanged) {
      void this.args.onChanged();
      return;
    }

    void this.router.refresh('coaching.coach.coachees');
  };

  <template>
    {{#if @sessions.length}}
      <div ...attributes>
        {{#if @title}}
          <h2 class="gud-eyebrow mb-3">{{@title}}</h2>
        {{/if}}
        <Await @promise={{this.cardItems @sessions}}>
          <:success as |items|>
            <div class="d-flex flex-column gap-4">
              {{#each items as |item|}}
                <SessionCard
                  @session={{item.session}}
                  @coach={{@coach}}
                  @coachee={{item.coachee}}
                  @coachingConfig={{item.coachingConfig}}
                  @onChanged={{this.refresh}}
                />
              {{/each}}
            </div>
          </:success>
          <:pending>
            <Spinner />
          </:pending>
        </Await>
      </div>
    {{else if @emptyMessage}}
      <EmptyState @message={{@emptyMessage}} />
    {{/if}}
  </template>
}
